import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { AddModules } from 'app/library/module/actions/add-entities.action';
import { Module } from 'app/library/module/models/module.model';
import { newModule } from 'app/library/module/models/newModule';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { map } from 'rxjs/operators';
import { ModuleState } from '../../module/models/module.state';
import {
    CreateCourseOfferingExecution,
    CreateCourseOfferingExecutionFailure,
    CreateCourseOfferingExecutionSuccess,
    FetchAllCourseOfferingExecutionsVersions,
    FetchAllCourseOfferingExecutionsVersionsFailure,
    FetchAllCourseOfferingExecutionsVersionsSuccess,
    FetchCourseOfferingExecutions,
    FetchCourseOfferingExecutionsFailure,
    FetchCourseOfferingExecutionsSearch,
    FetchCourseOfferingExecutionsSearchFailure,
    FetchCourseOfferingExecutionsSearchSuccess,
    FetchCourseOfferingExecutionsSuccess,
    FetchProgramExecutionCourseOfferingExecutions,
    FetchProgramExecutionCourseOfferingExecutionsFailure,
    FetchProgramExecutionCourseOfferingExecutionsSuccess,
    SearchCourseOfferingExecutions,
    SetCourseOfferingExecutions,
    SortCourseOfferingExecutions,
    UnsetCourseOfferingExecutions,
} from '../actions';
import { CourseOfferingExecutionsApiService } from '../services/course-offering-executions.api-service';
import { CourseOfferingExecutionsStateModel } from './course-offering-executions.state-model';

@State<CourseOfferingExecutionsStateModel>({
    name: 'courseOfferingExecutions',
    defaults: new CourseOfferingExecutionsStateModel(),
})
@Injectable()
export class CourseOfferingExecutionsState extends SubentityState<Module> {
    @Selector([ModuleState.getEntities])
    static getEntities(state: CourseOfferingExecutionsStateModel, modules: Module[]): Module[] {
        if (!modules) {
            return null;
        }

        return modules
            .filter((item) => state.list.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, Module], b: [number, Module]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, Module]) => x[1]);
    }

    @Selector([ModuleState.getEntities])
    static getFilteredEntities(state: CourseOfferingExecutionsStateModel, modules: Module[]): Module[] {
        if (!modules) {
            return null;
        }

        return modules
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, Module], b: [number, Module]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, Module]) => x[1]);
    }

    @Selector()
    static getTotalCount(state: CourseOfferingExecutionsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static getSortBy(state: CourseOfferingExecutionsStateModel): string {
        return state.sortBy;
    }

    @Selector()
    static getSortOrder(state: CourseOfferingExecutionsStateModel): SortDirection {
        return state.sortOrder;
    }

    @Selector()
    static getPageSize(state: CourseOfferingExecutionsStateModel): number {
        return state.pageSize;
    }

    @Selector()
    static getPageNumber(state: CourseOfferingExecutionsStateModel): number {
        return state.pageNumber || 1;
    }

    @Selector()
    static getPageIndex(state: CourseOfferingExecutionsStateModel): number {
        return state.pageNumber ? state.pageNumber - 1 : 0;
    }

    @Selector()
    static isFetchingList(state: CourseOfferingExecutionsStateModel): boolean {
        return state.fetchingList;
    }

    @Selector()
    static getSearchTerm(state: CourseOfferingExecutionsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getSelectedEntityIds(state: CourseOfferingExecutionsStateModel): string[] {
        return state.selected;
    }

    constructor(private _courseOfferingExecutionsService: CourseOfferingExecutionsApiService, private store: Store, private action$: Actions) {
        super(null, 0);

        this._propstoSearch = ['module', ...Module.props_to_search];
    }

    @Action(SetCourseOfferingExecutions)
    setAll(ctx: StateContext<CourseOfferingExecutionsStateModel>, action: SetCourseOfferingExecutions): void {
        this._setSubentities(ctx, action, AddModules);
    }

    @Action(UnsetCourseOfferingExecutions)
    unsetAll(ctx: StateContext<CourseOfferingExecutionsStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(FetchCourseOfferingExecutions)
    fetchAll(ctx: StateContext<CourseOfferingExecutionsStateModel>, action: FetchCourseOfferingExecutions): void {
        ctx.patchState({
            pageSize: action.payload.pageSize,
            pageNumber: action.payload.pageNumber,
        });

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCourseOfferingExecutions,
            FetchCourseOfferingExecutionsSuccess,
            FetchCourseOfferingExecutionsFailure,
            this._courseOfferingExecutionsService
                .getCourseOfferingExecutions(
                    action.payload.courseOfferingId,
                    action.payload.search,
                    action.payload.sortBy,
                    action.payload.sortOrder,
                    action.payload.pageSize,
                    action.payload.pageNumber,
                    action.payload.statusesIds,
                    action.payload.tags,
                    action.payload.extendedAttributesFilters,
                    action.payload.columns,
                    action.payload.context
                )
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => newModule(query));
                    })
                )
        );
    }

    @Action(FetchCourseOfferingExecutionsSuccess)
    fetchAllSuccess(ctx: StateContext<CourseOfferingExecutionsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchCourseOfferingExecutionsFailure)
    fetchAllFailure(ctx: StateContext<CourseOfferingExecutionsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(FetchProgramExecutionCourseOfferingExecutions)
    fetchProgramExecutionCourseOfferingExecutions(ctx: StateContext<CourseOfferingExecutionsStateModel>, action: FetchProgramExecutionCourseOfferingExecutions): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCourseOfferingExecutions,
            FetchProgramExecutionCourseOfferingExecutionsSuccess,
            FetchProgramExecutionCourseOfferingExecutionsFailure,
            this._courseOfferingExecutionsService
                .getProgramExecutionCourseOfferingExecutions(
                    action.payload.courseOfferingId,
                    action.payload.programExecutionId,
                    action.payload.scope,
                    action.payload.search,
                    action.payload.pageSize,
                    action.payload.pageNumber,
                    action.payload.statusesIds,
                    action.payload.context
                )
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => newModule(query));
                    })
                )
        );
    }

    @Action(FetchProgramExecutionCourseOfferingExecutionsSuccess)
    fetchProgramExecutionCourseOfferingExecutionsSuccess(ctx: StateContext<CourseOfferingExecutionsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchProgramExecutionCourseOfferingExecutionsFailure)
    fetchProgramExecutionCourseOfferingExecutionsFailure(ctx: StateContext<CourseOfferingExecutionsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(SortCourseOfferingExecutions)
    sort({ patchState }: StateContext<CourseOfferingExecutionsStateModel>, { payload }: SortCourseOfferingExecutions): void {
        sessionStorage.setItem(payload.storageActiveKey, payload.sort.active);
        sessionStorage.setItem(payload.storageDirectionKey, payload.sort.direction);

        patchState({
            sortBy: payload.sort.active,
            sortOrder: payload.sort.direction,
        });
    }

    @Action(CreateCourseOfferingExecution)
    createCourseOfferingExecution({ dispatch }: StateContext<CourseOfferingExecutionsStateModel>, { payload }: CreateCourseOfferingExecution): void {
        this._courseOfferingExecutionsService.createCourseOfferingExecution(payload).subscribe(
            (entity) => {
                dispatch(
                    new CreateCourseOfferingExecutionSuccess({
                        id: entity.id,
                        asyncJobId: entity.async_job_id,
                        courseOfferingId: payload.courseOfferingId,
                    })
                );
            },
            (response: HttpErrorResponse) => {
                dispatch(new CreateCourseOfferingExecutionFailure({ response }));
            }
        );
    }

    @Action(FetchCourseOfferingExecutionsSearch)
    fetchCourseOfferingsByCategorySearch({ dispatch }: StateContext<CourseOfferingExecutionsStateModel>, { payload }: FetchCourseOfferingExecutionsSearch): void {
        this._courseOfferingExecutionsService
            .findCourseOfferingExecutionsSearch(payload.courseOfferingId, payload.programExecutionId, payload.tags, payload.searchTerm, payload.users)
            .subscribe(
                (entity) => {
                    dispatch(new FetchCourseOfferingExecutionsSearchSuccess(entity));
                },
                (response: HttpErrorResponse) => {
                    dispatch(new FetchCourseOfferingExecutionsSearchFailure());
                }
            );
    }

    @Action(FetchAllCourseOfferingExecutionsVersions)
    fetchAllCourseOfferingExecutionsVersions({ dispatch }: StateContext<CourseOfferingExecutionsStateModel>, action: FetchAllCourseOfferingExecutionsVersions): void {
        this._courseOfferingExecutionsService.getAllCourseOfferingExecutionsVersions().subscribe(
            (entity) => {
                dispatch(new FetchAllCourseOfferingExecutionsVersionsSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchAllCourseOfferingExecutionsVersionsFailure());
            }
        );
    }

    @Action(SearchCourseOfferingExecutions)
    search({ patchState }: StateContext<CourseOfferingExecutionsStateModel>, { payload }: SearchCourseOfferingExecutions): void {
        sessionStorage.setItem(payload.sessionStorageKey, payload.searchItem);

        patchState({
            searchTerm: payload.searchItem,
        });
    }
}
